/**
 * Validated Schedule Form
 * 
 * Form for creating and editing schedules with full AJV validation and cron support
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Group,
    JsonInput,
    NumberInput,
    Paper,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import {
    IconCheck,
    IconClock,
    IconAlertTriangle,
    IconInfoCircle,
    IconX
} from '@tabler/icons-react';
import React, { useState, useEffect } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';

interface ScheduleData {
    name: string;
    description: string;
    cronExpression: string;
    timezone: string;
    isActive: boolean;
    maxRuns?: number;
    retryPolicy?: {
        maxRetries: number;
        retryDelay: number;
        backoffMultiplier: number;
    };
    metadata?: {
        tags: string[];
        priority: 'low' | 'normal' | 'high';
        [key: string]: any;
    };
}

interface ValidatedScheduleFormProps {
    initialData?: Partial<ScheduleData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: ScheduleData) => void;
    onCancel?: () => void;
}

export const ValidatedScheduleForm: React.FC<ValidatedScheduleFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    // State for advanced options and cron preview
    const [showAdvanced, setShowAdvanced] = useState(!!initialData?.retryPolicy);
    const [cronPreview, setCronPreview] = useState<string>('');
    const [isValidCron, setIsValidCron] = useState(true);

    // Initialize validated form
    const form = useValidatedForm<Partial<ScheduleData>>({
        schemaName: 'Schedule',
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            cronExpression: initialData?.cronExpression || '0 0 * * *',
            timezone: initialData?.timezone || 'UTC',
            isActive: initialData?.isActive ?? true,
            maxRuns: initialData?.maxRuns,
            retryPolicy: initialData?.retryPolicy || {
                maxRetries: 3,
                retryDelay: 1000,
                backoffMultiplier: 2
            },
            metadata: initialData?.metadata || {
                tags: [],
                priority: 'normal'
            }
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Mock mutation hook (replace with actual implementation)
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Schedule',
        () => ({
            mutate: async (data: Partial<ScheduleData>) => {
                // Mock implementation - replace with actual GraphQL mutation
                console.log('Submitting schedule:', data);
                return new Promise(resolve => setTimeout(() => resolve(data), 1000));
            },
            loading: false,
            error: null
        })
    );

    // Timezone options (common ones)
    const timezoneOptions = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Australia/Sydney'
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority' },
        { value: 'normal', label: 'Normal Priority' },
        { value: 'high', label: 'High Priority' }
    ];

    // Cron presets
    const cronPresets = [
        { label: 'Every minute', value: '* * * * *' },
        { label: 'Every 5 minutes', value: '*/5 * * * *' },
        { label: 'Every hour', value: '0 * * * *' },
        { label: 'Daily at midnight', value: '0 0 * * *' },
        { label: 'Daily at 9 AM', value: '0 9 * * *' },
        { label: 'Weekly on Monday', value: '0 0 * * 1' },
        { label: 'Monthly on 1st', value: '0 0 1 * *' }
    ];

    // Update cron preview when expression changes
    useEffect(() => {
        const cron = form.values.cronExpression;
        if (cron) {
            try {
                // Basic cron validation and description generation
                const parts = cron.split(' ');
                if (parts.length === 5 || parts.length === 6) {
                    setCronPreview(generateCronDescription(cron));
                    setIsValidCron(true);
                } else {
                    setCronPreview('Invalid cron expression');
                    setIsValidCron(false);
                }
            } catch {
                setCronPreview('Invalid cron expression');
                setIsValidCron(false);
            }
        }
    }, [form.values.cronExpression]);

    // Generate human-readable cron description
    const generateCronDescription = (cronExpr: string): string => {
        const [minute, hour, day, month, weekday] = cronExpr.split(' ');
        
        if (cronExpr === '* * * * *') return 'Every minute';
        if (cronExpr === '0 * * * *') return 'Every hour';
        if (cronExpr === '0 0 * * *') return 'Daily at midnight';
        if (cronExpr === '0 9 * * *') return 'Daily at 9:00 AM';
        if (cronExpr === '0 0 * * 1') return 'Weekly on Monday at midnight';
        if (cronExpr === '0 0 1 * *') return 'Monthly on the 1st at midnight';
        
        return `At ${minute === '*' ? 'every minute' : minute} past ${hour === '*' ? 'every hour' : `hour ${hour}`}`;
    };

    // Handle form submission
    const handleSubmit = async (values: Partial<ScheduleData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<ScheduleData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Title order={3}>
                                {mode === 'create' ? 'Create' : 'Edit'} Schedule
                            </Title>
                            <Text size="sm" c="dimmed">
                                Schedule configuration with cron expression validation
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Basic Information */}
                <Card withBorder>
                    <Title order={4} mb="md">Basic Information</Title>
                    <Stack gap="md">
                        <TextInput
                            label="Schedule Name"
                            placeholder="Daily backup process"
                            required
                            {...form.getInputProps('name')}
                            error={form.getFieldError('name')}
                            rightSection={
                                form.isFieldValid('name') && form.values.name ? 
                                <IconCheck size={16} color="green" /> : undefined
                            }
                        />

                        <Textarea
                            label="Description"
                            placeholder="Describe what this schedule does..."
                            minRows={2}
                            {...form.getInputProps('description')}
                            error={form.getFieldError('description')}
                        />

                        <Group grow>
                            <Select
                                label="Timezone"
                                data={timezoneOptions}
                                searchable
                                {...form.getInputProps('timezone')}
                                error={form.getFieldError('timezone')}
                            />
                            
                            <Switch
                                label="Schedule Active"
                                description="Enable/disable this schedule"
                                {...form.getInputProps('isActive', { type: 'checkbox' })}
                                mt="lg"
                            />
                        </Group>
                    </Stack>
                </Card>

                {/* Cron Configuration */}
                <Card withBorder>
                    <Title order={4} mb="md">Schedule Configuration</Title>
                    <Stack gap="md">
                        <Select
                            label="Cron Preset"
                            placeholder="Choose a preset or enter custom"
                            data={cronPresets}
                            clearable
                            onChange={(value: string | null) => {
                                if (value) {
                                    form.setFieldValue('cronExpression', value);
                                }
                            }}
                        />

                        <TextInput
                            label="Cron Expression"
                            placeholder="0 0 * * *"
                            required
                            {...form.getInputProps('cronExpression')}
                            error={form.getFieldError('cronExpression') || (!isValidCron ? 'Invalid cron format' : undefined)}
                            rightSection={
                                isValidCron ? <IconClock size={16} color="green" /> : <IconX size={16} color="red" />
                            }
                        />

                        {cronPreview && (
                            <Alert
                                color={isValidCron ? 'blue' : 'red'}
                                title="Schedule Preview"
                                icon={<IconInfoCircle />}
                            >
                                {cronPreview}
                            </Alert>
                        )}

                        <NumberInput
                            label="Maximum Runs (Optional)"
                            placeholder="Leave empty for unlimited"
                            min={1}
                            {...form.getInputProps('maxRuns')}
                            error={form.getFieldError('maxRuns')}
                        />
                    </Stack>
                </Card>

                {/* Advanced Options */}
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={4}>Advanced Options</Title>
                        <Switch
                            label="Show Advanced"
                            checked={showAdvanced}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowAdvanced(e.currentTarget.checked)}
                        />
                    </Group>

                    {showAdvanced && (
                        <Stack gap="md">
                            {/* Retry Policy */}
                            <div>
                                <Text fw={500} mb="sm">Retry Policy</Text>
                                <Group grow>
                                    <NumberInput
                                        label="Max Retries"
                                        min={0}
                                        max={10}
                                        {...form.getInputProps('retryPolicy.maxRetries')}
                                        error={form.getFieldError('retryPolicy.maxRetries')}
                                    />
                                    <NumberInput
                                        label="Retry Delay (ms)"
                                        min={100}
                                        step={100}
                                        {...form.getInputProps('retryPolicy.retryDelay')}
                                        error={form.getFieldError('retryPolicy.retryDelay')}
                                    />
                                    <NumberInput
                                        label="Backoff Multiplier"
                                        min={1}
                                        max={5}
                                        step={0.1}
                                        decimalScale={1}
                                        {...form.getInputProps('retryPolicy.backoffMultiplier')}
                                        error={form.getFieldError('retryPolicy.backoffMultiplier')}
                                    />
                                </Group>
                            </div>

                            {/* Metadata */}
                            <div>
                                <Text fw={500} mb="sm">Metadata</Text>
                                <Stack gap="sm">
                                    <Select
                                        label="Priority"
                                        data={priorityOptions}
                                        {...form.getInputProps('metadata.priority')}
                                        error={form.getFieldError('metadata.priority')}
                                    />
                                    
                                    <TextInput
                                        label="Tags (comma-separated)"
                                        placeholder="backup, daily, critical"
                                        value={form.values.metadata?.tags?.join(', ') || ''}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                                            form.setFieldValue('metadata.tags', tags);
                                        }}
                                    />
                                </Stack>
                            </div>
                        </Stack>
                    )}
                </Card>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Schedule will be validated before saving
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid || !isValidCron}
                            >
                                {mode === 'create' ? 'Create Schedule' : 'Update Schedule'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Schedule saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedScheduleForm;